import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import type { components } from "../lib/api-types";

type TransformationProgram = components["schemas"]["TransformationProgram"];
type TransformationScopeCheckRequest = components["schemas"]["TransformationScopeCheckRequest"];
type TransformationScopeCheck = components["schemas"]["TransformationScopeCheck"];

const SCOPE_SAMPLES: Array<{ label: string; request: TransformationScopeCheckRequest }> = [
  {
    label: "Board Pack Export",
    request: {
      program_id: "kid021-oneandone-impulse",
      stream_id: "technik",
      phase: "move",
      document_class: "board_pack",
      role: "oneandone-board",
      action: "export",
    },
  },
  {
    label: "Read-only OWUI Wissen",
    request: {
      program_id: "kid021-oneandone-impulse",
      stream_id: "technik",
      phase: "freeze",
      document_class: "published_owui_knowledge",
      role: "oneandone-readonly-reviewer",
      action: "read",
    },
  },
  {
    label: "Externer Zugriff Diagnose-Rohdaten",
    request: {
      program_id: "kid021-oneandone-impulse",
      stream_id: "cm",
      phase: "unfreeze",
      document_class: "diagnostic_raw",
      role: "oneandone-external-consultant",
      action: "read",
    },
  },
  {
    label: "Communication Asset Publish",
    request: {
      program_id: "kid021-oneandone-impulse",
      stream_id: "customer_ops",
      phase: "move",
      document_class: "communication_asset",
      role: "impulse-consultant",
      action: "client_publish",
    },
  },
];

export default function TransformationOSPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { effectiveTenantId } = useTenant();
  const program = useQuery({
    queryKey: ["transformation-program", effectiveTenantId],
    queryFn: () => api.get<TransformationProgram>("/transformation/program"),
  });
  const scopeChecks = useQuery({
    queryKey: ["transformation-scope-check-samples", effectiveTenantId],
    queryFn: () =>
      Promise.all(
        SCOPE_SAMPLES.map(async (sample) => ({
          label: sample.label,
          result: await api.post<TransformationScopeCheck>("/transformation/scope-check", sample.request),
        })),
      ),
  });
  const scopeHistory = useQuery({
    queryKey: ["transformation-scope-check-history", effectiveTenantId, scopeChecks.data?.map((item) => item.result.id).join(",")],
    queryFn: () => api.get<TransformationScopeCheck[]>("/transformation/scope-checks?limit=6"),
    enabled: scopeChecks.isSuccess,
  });
  const decideScope = useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: "approve" | "reject"; reason?: string }) => {
      const query = action === "reject" && reason ? `?reason=${encodeURIComponent(reason)}` : "";
      return api.post<TransformationScopeCheck>(`/transformation/scope-checks/${id}/${action}${query}`, {});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["transformation-scope-check-history", effectiveTenantId] }),
  });
  const data = program.data;

  return (
    <section className="flow-page transformation-page">
      <header className="flow-hero transformation-hero">
        <div>
          <p className="hero-eyebrow">Transformation OS / 1&1 IMPULSE</p>
          <h1>Transformation wird als Programm, Stream, Phase und Evidence steuerbar.</h1>
          <p className="hero-sub">
            CMB, KEP und Blue werden nicht als Dokumentablage modelliert, sondern als rollenbasierte Arbeitsräume mit Agenten,
            Wirkungslogik, Approval und tenant-sicherer Evidenz.
          </p>
        </div>
        <div className="flow-score">
          <span>Scope Key</span>
          <strong>{data ? data.streams.length : "5"} Streams</strong>
          <small>{data?.program_id ?? "kid021-oneandone-impulse"}</small>
        </div>
      </header>

      {program.isLoading && <div className="flow-panel">Transformation-Contract wird geladen...</div>}
      {program.isError && <div className="flow-panel form-error">Transformation-Contract konnte nicht geladen werden.</div>}

      {data && (
        <>
          <section className="flow-panel transformation-access">
            <div className="panel-heading">
              <div>
                <h2>{data.title}</h2>
                <p>{data.positioning}</p>
              </div>
              <span className="status-pill good">Read-only Contract</span>
            </div>
            <code>{data.access_key}</code>
            <div className="flow-actions">
              <Link className="btn primary" to="/app/process-landscape">Prozesslandschaft öffnen</Link>
              <Link className="btn ghost" to="/app/agents?agent=governance">Governance-Runs</Link>
            </div>
          </section>

          <div className="transformation-strands">
            {data.strands.map((strand) => (
              <article className="flow-panel transformation-strand" key={strand.id}>
                <span className="hero-eyebrow">{strand.id.toUpperCase()}</span>
                <h2>{strand.label}</h2>
                <p>{strand.purpose}</p>
                <div className="transformation-meta"><span>Lead</span><strong>{strand.lead}</strong></div>
                <div className="transformation-meta"><span>KPI-Wirkung</span><strong>{strand.kpi_angle}</strong></div>
              </article>
            ))}
          </div>

          <section className="flow-panel">
            <div className="panel-heading">
              <div>
                <h2>Streams / Ressorts</h2>
                <p>Jeder Stream erhält einen eigenen Arbeitsraum, eigene Evidence und eigene Rollen-/Dokumentklassen-Gates.</p>
              </div>
            </div>
            <div className="transformation-stream-grid">
              {data.streams.map((stream) => (
                <article key={stream.id}>
                  <h3>{stream.label}</h3>
                  <ul>
                    {stream.themes.map((theme) => <li key={theme}>{theme}</li>)}
                  </ul>
                </article>
              ))}
            </div>
          </section>

          <section className="flow-panel">
            <div className="panel-heading">
              <div>
                <h2>Lewin-Phasen und Arbeitsschritte</h2>
                <p>Die CMB-Prozessstrecke wird mit Mobilisierung, Professionalisierung und Stabilisierung überlagert.</p>
              </div>
            </div>
            <div className="transformation-phase-grid">
              {data.phases.map((phase) => (
                <article key={phase.id}>
                  <span>{phase.question}</span>
                  <h3>{phase.label}</h3>
                  <p>{phase.outcomes.join(" · ")}</p>
                </article>
              ))}
            </div>
            <ol className="transformation-steps">
              {data.work_steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
          </section>

          <section className="flow-panel">
            <div className="panel-heading">
              <div>
                <h2>Agenten und OWUI-Personas</h2>
                <p>Open WebUI bleibt ein rollenbeschränkter Zugang auf freigegebene Knowledge Collections, nicht die Quelle der Wahrheit.</p>
              </div>
            </div>
            <div className="transformation-agent-grid">
              {data.agents.map((agent) => (
                <article key={agent.id}>
                  <h3>{agent.label}</h3>
                  <p>{agent.mission}</p>
                  <span>{agent.tools.join(" · ")}</span>
                </article>
              ))}
            </div>
            <div className="persona-strip">
              {data.owui_personas.map((persona) => <span key={persona}>{persona}</span>)}
            </div>
          </section>

          <section className="flow-panel">
            <div className="panel-heading">
              <div>
                <h2>Document-Class Gates</h2>
                <p>Export, Sharing, Prompt-Auswertung, Deck-Erzeugung und OWUI-Publish laufen abhängig von Datenklasse und Rolle.</p>
              </div>
            </div>
            <div className="doc-class-table">
              {data.document_classes.map((item) => (
                <article key={item.id}>
                  <div>
                    <strong>{item.label}</strong>
                    <span>{item.id}</span>
                  </div>
                  <span className={`status-pill ${item.external_llm_allowed ? "warning" : "good"}`}>
                    {item.external_llm_allowed ? "external gated" : "local only"}
                  </span>
                  <p>{item.approval_required.join(" · ")}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="flow-panel">
            <div className="panel-heading">
              <div>
                <h2>Scope Guard Matrix</h2>
                <p>Jeder Zugriff wird gegen Tenant, Programm, Stream, Phase, Dokumentklasse, Rolle und Aktion entschieden.</p>
              </div>
              <span className="status-pill good">Tenant scoped</span>
            </div>
            <div className="scope-check-grid">
              {scopeChecks.data?.map(({ label, result }) => (
                <article className={`scope-check-card decision-${result.decision}`} key={label}>
                  <div className="scope-check-top">
                    <div>
                      <strong>{label}</strong>
                      <span>{result.stream_id} · {result.phase} · {result.document_class}</span>
                    </div>
                    <span
                      className={`status-pill ${
                        result.decision === "allow" ? "good" : result.decision === "approval_required" ? "warning" : "danger"
                      }`}
                    >
                      {result.decision}
                    </span>
                  </div>
                  <p>{result.reasons.length ? result.reasons.join(" · ") : "Tenant-Scope validiert, Zugriff erlaubt."}</p>
                  <code>{result.role} / {result.action}</code>
                </article>
              ))}
            </div>
            {scopeChecks.isLoading && <p className="empty-state">Scope Checks werden geladen...</p>}
            {scopeChecks.isError && <p className="form-error">Scope Guard konnte nicht geladen werden.</p>}
            <div className="scope-log-list">
              <div className="scope-log-head">
                <strong>Auditierter Decision Log</strong>
                <span>{scopeHistory.data?.length ?? 0} tenant-lokale Einträge</span>
              </div>
              {scopeHistory.data?.map((item) => (
                <article key={item.id}>
                  <span className={`status-pill ${item.decision === "allow" ? "good" : item.decision === "approval_required" ? "warning" : "danger"}`}>
                    {item.decision}
                  </span>
                  <div>
                    <strong>{item.document_class}</strong>
                    <p>
                      {item.role} / {item.action} · {item.stream_id} · {item.phase}
                      {item.approval?.required && ` · ${item.approval?.status}`}
                    </p>
                  </div>
                  <code>{item.evidence_refs[0] ?? item.id}</code>
                  {(item.approval?.status === "pending" || item.run_id || item.evidence_id) && (
                    <div className="scope-approval-actions" aria-label={`Scope-Entscheidung ${item.id}`}>
                      {item.evidence_id && (
                        <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>
                          Evidence
                        </Link>
                      )}
                      {item.run_id && (
                        <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(item.run_id)}`}>
                          Run
                        </Link>
                      )}
                      {item.approval?.status === "pending" && (
                        <>
                          <button
                            className="btn good small"
                            type="button"
                            disabled={decideScope.isPending}
                            onClick={() => decideScope.mutate({ id: item.id, action: "approve" })}
                          >
                            Freigeben
                          </button>
                          <button
                            className="btn bad small"
                            type="button"
                            disabled={decideScope.isPending}
                            onClick={() => decideScope.mutate({ id: item.id, action: "reject", reason: "needs-owner-review" })}
                          >
                            Ablehnen
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </article>
              ))}
              {scopeHistory.isLoading && <p className="empty-state">Decision Log wird geladen...</p>}
              {scopeHistory.isError && <p className="form-error">Decision Log konnte nicht geladen werden.</p>}
            </div>
          </section>

          <section className="flow-panel">
            <div className="panel-heading">
              <div>
                <h2>Pipeline und Guardrails</h2>
                <p>Nextcloud, Obsidian und OWUI werden erst nach Klassifizierung, ACL und Evidence in den Agentenfluss aufgenommen.</p>
              </div>
            </div>
            <div className="pipeline-strip">
              {data.pipeline.map((step) => <span key={step}>{step}</span>)}
            </div>
            <ul className="evidence-list">
              {data.guardrails.map((guardrail) => <li key={guardrail}>{guardrail}</li>)}
              {data.evidence_refs.map((ref) => <li key={ref}>{ref}</li>)}
            </ul>
          </section>
        </>
      )}
    </section>
  );
}
